import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import styles from './Analytics.module.css';
import { TargetIcon, ClipboardIcon, SearchIcon, ChartIcon, BookIcon, CalculatorIcon } from './Icons';
import { useAutoSave } from '../hooks/useAutoSave';
import CollegeCard from './CollegeCard';
import ProfilePanel from './ProfilePanel';
import FloatingActionButton from './FloatingActionButton';
import Toast from './Toast';

const Analytics = () => {
  const { isAuthenticated, userId } = useAuth0();
  const [myList, setMyList] = useState([]);
  const [collegeActivities, setCollegeActivities] = useState([]);
  const [academicProfile, setAcademicProfile] = useState({});
  const [isProfilePanelOpen, setIsProfilePanelOpen] = useState(false);
  const [toast, setToast] = useState(null);

  // Auto-save hooks
  useAutoSave('myCollegeList', myList);
  useAutoSave('collegeActivities', collegeActivities);
  useAutoSave('academicProfile', academicProfile);

  useEffect(() => {
    const loadList = () => {
      const savedList = JSON.parse(localStorage.getItem('myCollegeList')) || [];
      setMyList(savedList);
    };

    const loadActivities = () => {
      try {
        const savedActivities = JSON.parse(localStorage.getItem('collegeActivities')) || [];
        setCollegeActivities(savedActivities);
        console.log('[Analytics] Loaded activities:', savedActivities.length, 'activities');
      } catch (error) {
        console.error('[Analytics] Error loading activities:', error);
        setCollegeActivities([]);
      }
    };

    const loadAcademicProfile = () => {
      const savedProfile = JSON.parse(localStorage.getItem('academicProfile')) || {};
      setAcademicProfile(savedProfile);
    };

    loadList();
    loadActivities();
    loadAcademicProfile();

    const handleStorageChange = (e) => {
      if (e.key === 'myCollegeList') {
        loadList();
      }
      if (e.key === 'collegeActivities') {
        loadActivities();
      }
      if (e.key === 'academicProfile') {
        loadAcademicProfile();
      }
    };

    const handleActivitiesUpdate = (event) => {
      console.log('[Analytics] Activities updated, refreshing data');
      loadActivities();
    };

    // Listen for academic profile updates
    const handleAcademicProfileUpdate = () => {
      console.log('[Analytics] Academic profile updated, refreshing data');
      loadAcademicProfile();
    };

    // --- Add this: Listen for collegeListUpdated event in same tab ---
    const handleCollegeListUpdate = () => {
      loadList();
    };
    window.addEventListener('collegeListUpdated', handleCollegeListUpdate);
    // --- End add ---

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('collegeActivitiesUpdated', handleActivitiesUpdate);
    window.addEventListener('academicProfileUpdated', handleAcademicProfileUpdate);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('collegeActivitiesUpdated', handleActivitiesUpdate);
      window.removeEventListener('academicProfileUpdated', handleAcademicProfileUpdate);
      // --- Remove the event listener on cleanup ---
      window.removeEventListener('collegeListUpdated', handleCollegeListUpdate);
    };
  }, []);

  const handleRemoveFromList = (collegeName) => {
    try {
      const updatedList = myList.filter(c => c.name !== collegeName);
      setMyList(updatedList);
      // Update localStorage
      localStorage.setItem('myCollegeList', JSON.stringify(updatedList));
      // Dispatch custom event to notify other components
      window.dispatchEvent(new Event('collegeListUpdated'));
      // Show success toast
      setToast({
        message: `${collegeName} removed from your list`,
        type: 'success'
      });
    } catch (error) {
      console.error('Error removing college from list:', error);
      setToast({
        message: 'Error removing college from list',
        type: 'error'
      });
    }
  };

  const closeToast = () => {
    setToast(null);
  };

  const handleSaveProfile = (profile) => {
    setAcademicProfile(profile);
    // Save to localStorage
    localStorage.setItem('academicProfile', JSON.stringify(profile));
    // Dispatch custom event to notify other components
    window.dispatchEvent(new Event('academicProfileUpdated'));
    // Don't automatically close the panel - let user close it manually
    // setIsProfilePanelOpen(false);
  };

  // Calculate activity statistics
  const totalActivities = collegeActivities.length;
  const totalHours = collegeActivities.reduce((sum, activity) => {
    return sum + ((activity.hoursPerWeek || 0) * (activity.weeksPerYear || 36));
  }, 0);
  const leadershipPositions = collegeActivities.filter(activity => 
    (activity.leadershipRole || 0) >= 1
  ).length;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h1 className={styles.title}>My College Analytics</h1>
        <p className={styles.subtitle}>
          Track your progress and analyze your chances with your saved colleges and activities.
        </p>
      </div>

      {/* Academic Profile Button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1.5rem' }}>
        <button className={styles.editButton} onClick={() => setIsProfilePanelOpen(true)}>
          Edit Academic Profile
        </button>
      </div>

      <div className={styles.collegeSection}>
        <h2 className={styles.sectionTitle}>My College List</h2>
        {myList.length > 0 ? (
          <div className={styles.collegeGrid}>
            {myList.map((college) => (
              <div key={college.name} className={styles.cardWrapper}>
                <CollegeCard 
                  college={college} 
                  onRemove={() => handleRemoveFromList(college.name)}
                />
                <div className={styles.buttonContainer}>
                  <Link to={`/college-analytics/${college.name}`} className={styles.analyticsBtn}>
                    See Personalized Recommendations
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className={styles.emptyState}>
            <h3>You don't have any colleges on your list yet.</h3>
            <p>Go to the Explore page to browse and add colleges to your list.</p>
            <Link to="/explore" className={styles.exploreBtn}>
              Explore Colleges
            </Link>
          </div>
        )}
      </div>

      {/* Profile Panel Modal */}
      <ProfilePanel
        isOpen={isProfilePanelOpen}
        onClose={() => setIsProfilePanelOpen(false)}
        onSave={handleSaveProfile}
        initialProfile={academicProfile}
      />

      {/* Floating Action Button */}
      <FloatingActionButton 
        onClick={() => setIsProfilePanelOpen(true)}
        isProfilePanelOpen={isProfilePanelOpen}
      />
      
      {/* Toast Notification */}
      {toast && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={closeToast}
        />
      )}
    </div>
  );
};

export default Analytics;